import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { mediaUrl } from "@/lib/api";
import Reveal from "@/components/shared/Reveal";
import { useSettings } from "@/components/layout/PublicLayout";
import Seo from "@/components/shared/Seo";
import { Coffee, Wine, Clock, Download } from "lucide-react";

const typeMeta = {
  pause: { icon: Coffee, tint: "text-f2b-gold" },
  cocktail: { icon: Wine, tint: "text-f2b-gold" },
};

const Programme = () => {
  const settings = useSettings();
  const t = settings?.pages?.programme || {};
  const { data: editions = [] } = useQuery({ queryKey: ["editions"], queryFn: async () => (await api.get("/editions")).data });
  const [params] = useSearchParams();
  const editionSlug = params.get("edition");
  const active = editions.find((e) => e.is_active) || editions.find((e) => e.status === "active") || editions.find((e) => e.status === "upcoming");
  const ed2026 = (editionSlug && editions.find((e) => e.slug === editionSlug)) || active;
  const { data: confs = [], isLoading } = useQuery({
    queryKey: ["conferences", ed2026?.id],
    queryFn: async () => (await api.get(`/conferences?edition_id=${ed2026.id}`)).data,
    enabled: !!ed2026?.id,
  });

  const allTags = Array.from(new Set(confs.flatMap((c) => c.theme_tags || [])));
  const [filter, setFilter] = useState("all");
  const shown = confs.filter((c) => filter === "all" || (c.theme_tags || []).includes(filter));

  return (
    <>
      <Seo title={t.seo_title || `Programme${ed2026?.year ? " " + ed2026.year : ""} — FACADES2build`} description={t.seo_description || ed2026?.summary || "Le programme de l'édition en cours de FACADES2build."} />

      <section className="bg-f2b-black pt-40 pb-16">
        <div className="f2b-container">
          <Reveal><p className="f2b-kicker text-f2b-cyan mb-4">{t.kicker || (ed2026?.year ? `Édition ${ed2026.year}` : "Programme")}</p></Reveal>
          <Reveal delay={0.08}>
            <h1 className="font-display font-bold text-white text-4xl md:text-6xl leading-tight max-w-3xl">
              {t.title || (ed2026?.theme ? `Programme — ${ed2026.theme}` : (ed2026?.year ? `Programme ${ed2026.year}` : "Programme"))}
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="text-white/70 mt-6 max-w-2xl leading-relaxed">
              {t.lead || ed2026?.summary || "Le programme de cette édition sera dévoilé prochainement."}
            </p>
          </Reveal>
        </div>
      </section>

      {/* filters */}
      <section className="bg-f2b-light border-b border-f2b-filet sticky top-0 z-30 py-4">
        <div className="f2b-container flex flex-wrap gap-2">
          <button onClick={() => setFilter("all")} data-testid="program-filter-all"
            className={`px-4 py-2 text-sm border transition-colors ${filter === "all" ? "bg-f2b-black text-white border-f2b-black" : "bg-white text-f2b-gray border-f2b-filet hover:border-f2b-cyan"}`}>
            Tout
          </button>
          {allTags.map((t) => (
            <button key={t} onClick={() => setFilter(t)} data-testid={`program-filter-${t}`}
              className={`px-4 py-2 text-sm border transition-colors ${filter === t ? "bg-f2b-black text-white border-f2b-black" : "bg-white text-f2b-gray border-f2b-filet hover:border-f2b-cyan"}`}>
              {t}
            </button>
          ))}
        </div>
      </section>

      {/* timeline */}
      <section className="py-20 bg-white">
        <div className="f2b-container">
          {isLoading ? (
            <p className="text-f2b-gray">Chargement…</p>
          ) : confs.length === 0 ? (
            <p className="text-f2b-gray" data-testid="programme-empty">Le programme de cette édition sera dévoilé prochainement.</p>
          ) : (
            <div className="relative border-l-2 border-f2b-filet ml-2 md:ml-0">
              {shown.map((c, i) => {
                const meta = typeMeta[c.type];
                const Icon = meta?.icon || Clock;
                const isEvent = c.type === "pause" || c.type === "cocktail";
                return (
                  <Reveal key={c.id} delay={i * 0.04} className="relative pl-8 md:pl-12 pb-12" data-testid={`program-item-${i}`}>
                    <span className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full ${isEvent ? "bg-f2b-gold" : "bg-f2b-cyan"}`} />
                    <div className={`${isEvent ? "bg-f2b-light" : ""} ${isEvent ? "p-6" : ""}`}>
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        {c.time_label && <span className="font-display font-bold text-f2b-cyan text-lg">{c.time_label}</span>}
                        {c.duration && <span className="text-xs text-f2b-gray border border-f2b-filet px-2 py-0.5">{c.duration}</span>}
                        {c.company && <span className="text-xs bg-f2b-gold/15 text-f2b-gold px-2 py-0.5 font-medium">{c.company}</span>}
                        {c.type === "focus" && <span className="text-xs text-f2b-gray uppercase tracking-wider">Focus technique</span>}
                        {isEvent && <Icon size={16} className={meta?.tint} />}
                      </div>
                      <h3 className={`font-display font-bold text-f2b-black leading-tight ${isEvent ? "text-xl" : "text-xl md:text-2xl"}`}>{c.title}</h3>
                      {c.speakers?.length > 0 && (
                        <p className="text-sm text-f2b-gray mt-2">
                          {c.speakers.map((s, idx) => (
                            <React.Fragment key={s.id}>
                              <Link to={`/intervenants/${s.slug}`} className="hover:text-f2b-cyan">{s.name}</Link>
                              {idx < c.speakers.length - 1 ? ", " : ""}
                            </React.Fragment>
                          ))}
                        </p>
                      )}
                      {c.summary && !isEvent && <p className="text-f2b-text/75 mt-3 max-w-2xl leading-relaxed">{c.summary}</p>}
                      {isEvent && c.summary && <p className="text-f2b-gray mt-2">{c.summary}</p>}
                      {c.key_takeaway && (
                        <p className="f2b-lead text-f2b-black mt-3 border-l-2 border-f2b-cyan pl-4 max-w-2xl">À retenir : {c.key_takeaway}</p>
                      )}
                      {c.theme_tags?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-4">
                          {c.theme_tags.map((t) => <span key={t} className="text-xs border border-f2b-filet text-f2b-gray px-2 py-1">{t}</span>)}
                        </div>
                      )}
                      {(c.article?.slug || c.document_url) && (
                        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-4">
                          {c.article?.slug && (
                            <Link to={`/blog/${c.article.slug}`} className="inline-flex items-center gap-1 text-sm text-f2b-cyan f2b-link-underline" data-testid={`conf-article-link-${i}`}>Lire l'article associé →</Link>
                          )}
                          {c.document_url && (
                            <a href={mediaUrl(c.document_url)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-f2b-cyan f2b-link-underline" data-testid={`conf-document-link-${i}`}><Download size={14} /> Télécharger la présentation</a>
                          )}
                        </div>
                      )}
                    </div>
                  </Reveal>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Programme;
